import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";

function GoogleIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.3-1.6 3.9-5.5 3.9-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.8 3.3 14.6 2.3 12 2.3 6.6 2.3 2.3 6.6 2.3 12s4.3 9.7 9.7 9.7c5.6 0 9.3-3.9 9.3-9.5 0-.6-.1-1.1-.2-1.6H12z" />
    </svg>
  );
}

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const emergentGoogleAuth = process.env.REACT_APP_ENABLE_EMERGENT_GOOGLE_AUTH === "true";

  if (user) return <Navigate to="/" replace />;

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome to MB NEXUS");
      nav("/");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0B1120] p-4 relative overflow-hidden">
      <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-amber-600/10 blur-3xl" />
      <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-blue-600/10 blur-3xl" />
      <div className="relative w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="relative h-12 w-12 flex items-center justify-center">
            <div className="absolute inset-0 rotate-45 rounded-lg bg-gradient-to-br from-amber-500 to-amber-700 shadow-lg shadow-amber-900/40" />
            <span className="relative text-slate-950 font-extrabold">MB</span>
          </div>
          <div>
            <div className="font-heading font-extrabold text-2xl tracking-tight text-white">NEXUS</div>
            <div className="caption-mono !text-[10px]">AI Real Estate Sales OS</div>
          </div>
        </div>

        <div className="mb-card p-7">
          <h1 className="font-heading text-xl font-bold text-slate-100">Sign in</h1>
          <p className="text-sm text-slate-400 mt-1 mb-6">Access your RAV sales command center.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Email</label>
              <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="w-full bg-slate-900/70 border border-slate-800 rounded-lg px-3 py-2.5 text-sm text-slate-200 focus:outline-none focus:border-amber-500/40" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Password</label>
              <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                className="w-full bg-slate-900/70 border border-slate-800 rounded-lg px-3 py-2.5 text-sm text-slate-200 focus:outline-none focus:border-amber-500/40" />
            </div>
            <button data-testid="login-submit" type="submit" disabled={loading}
              className="btn-gold w-full py-2.5 flex items-center justify-center gap-2">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Sign in
            </button>
          </form>
          {emergentGoogleAuth && <><div className="flex items-center gap-3 my-4"><div className="flex-1 h-px bg-slate-800" /><span className="text-[10px] text-slate-500 uppercase tracking-wider">or</span><div className="flex-1 h-px bg-slate-800" /></div>
          <button type="button" onClick={googleLogin} data-testid="google-login-btn"
            className="w-full py-2.5 rounded-lg border border-slate-700 bg-slate-900/60 hover:bg-slate-800 text-slate-200 text-sm flex items-center justify-center gap-2 transition-colors">
            <GoogleIcon /> Continue with Google
          </button></>}
        </div>
      </div>
    </div>
  );
}
